import { Info, Mail, MapPin, Phone, Printer } from 'lucide-react';

import type { DirectoryHandle } from '@/hooks/useProviderDirectory';
import type { TreatmentRecordRequestChannelType } from '@/services/api/generated/treatmentRecordRequestsService';

/**
 * Parse pipe-delimited address value into components.
 * Example: "address_line1=123 Main St|city=Boston|state=MA|zip=02101"
 */
export const parseMailingAddress = (
  value: string,
): {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
} => {
  const parts: Record<string, string> = {};

  value.split('|').forEach((part) => {
    const [key, val] = part.split('=');
    if (key && val) {
      parts[key.trim()] = val.trim();
    }
  });

  return {
    address_line1: parts.address_line1 || undefined,
    address_line2: parts.address_line2 || undefined,
    city: parts.city || undefined,
    state: parts.state || undefined,
    zip: parts.zip || undefined,
  };
};

/**
 * Format parsed address components into a human-readable string.
 */
export const formatMailingAddress = (components: {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip?: string;
}): string => {
  const parts = [components.address_line1];

  if (components.address_line2) {
    parts.push(components.address_line2);
  }

  if (components.city && components.state && components.zip) {
    parts.push(`${components.city}, ${components.state} ${components.zip}`);
  } else if (components.city || components.state || components.zip) {
    // Handle partial address data
    const cityState = [components.city, components.state]
      .filter(Boolean)
      .join(', ');
    if (cityState && components.zip) {
      parts.push(`${cityState} ${components.zip}`);
    } else if (cityState) {
      parts.push(cityState);
    } else if (components.zip) {
      parts.push(components.zip);
    }
  }

  return parts.filter(Boolean).join(', ');
};

/**
 * Format the display value for a directory contact handle.
 * Handles both simple values (fax, email, phone) and address components.
 */
export const formatDirectoryHandleValue = (handle: DirectoryHandle): string => {
  if (!handle.handle) {
    return 'No contact information';
  }

  const handleType = (handle.handle.handle_type || '').toUpperCase();

  // For MAILING_ADDRESS, parse and format the pipe-delimited value
  if (handleType === 'MAILING_ADDRESS' && handle.handle.value) {
    const addressComponents = parseMailingAddress(handle.handle.value);
    return formatMailingAddress(addressComponents);
  }

  // For other types (FAX, EMAIL, PHONE), return the value directly
  if (handle.handle.value) {
    return handle.handle.value;
  }

  return 'No contact information';
};

/**
 * Get the icon component for a contact handle type.
 */
export const getHandleTypeIcon = (type?: string) => {
  switch ((type || '').toUpperCase()) {
    case 'PHONE':
      return Phone;
    case 'FAX':
      return Printer;
    case 'EMAIL':
      return Mail;
    case 'MAILING_ADDRESS':
      return MapPin;
    default:
      return Info;
  }
};

/**
 * Get the icon color class for a contact handle type.
 */
export const getHandleTypeIconColor = (type?: string): string => {
  switch ((type || '').toUpperCase()) {
    case 'PHONE':
      return 'text-blue-600';
    case 'FAX':
      return 'text-purple-600';
    case 'EMAIL':
      return 'text-teal-600';
    case 'MAILING_ADDRESS':
      return 'text-amber-600';
    default:
      return 'text-gray-500';
  }
};

/**
 * Map a ContactHandle type to a TreatmentRecordRequest channel type.
 */
export const mapHandleTypeToChannelType = (
  handleType?: string | null,
): TreatmentRecordRequestChannelType | null => {
  switch ((handleType || '').toUpperCase()) {
    case 'FAX':
      return 'fax';
    case 'EMAIL':
      return 'email';
    case 'MAILING_ADDRESS':
      return 'mail';
    default:
      return null;
  }
};

/**
 * Format cadence information for tooltip display.
 */
export const formatCadenceTooltip = (
  handle: DirectoryHandle,
): string | undefined => {
  if (!handle.voice_ai_cadence_days) return undefined;
  return `Voice AI cadence: every ${handle.voice_ai_cadence_days} day${handle.voice_ai_cadence_days > 1 ? 's' : ''}`;
};

/**
 * Get a human-readable label for contact handle type.
 */
export const getHandleTypeLabel = (type?: string): string => {
  switch ((type || '').toUpperCase()) {
    case 'PHONE':
      return 'Phone';
    case 'FAX':
      return 'Fax';
    case 'EMAIL':
      return 'Email';
    case 'MAILING_ADDRESS':
      return 'Mailing Address';
    default:
      return 'Other';
  }
};

/**
 * Format unit type for display (e.g., "RECORDS_DEPARTMENT" → "Records Department").
 */
export const formatUnitType = (unitType?: string): string => {
  if (!unitType) return '';
  return unitType
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Sort handles by preference: preferred first, then by type priority.
 */
export const sortHandlesByPreference = (
  handles: DirectoryHandle[],
): DirectoryHandle[] => {
  const typePriority: Record<string, number> = {
    FAX: 1,
    EMAIL: 2,
    MAILING_ADDRESS: 3,
    PHONE: 4,
  };

  return [...handles].sort((a, b) => {
    // Preferred first
    if (a.is_preferred && !b.is_preferred) return -1;
    if (!a.is_preferred && b.is_preferred) return 1;

    // Then by type priority (fax, email, mail, phone)
    const aType = (a.handle?.handle_type || '').toUpperCase();
    const bType = (b.handle?.handle_type || '').toUpperCase();
    const aPriority = typePriority[aType] ?? 999;
    const bPriority = typePriority[bType] ?? 999;
    return aPriority - bPriority;
  });
};
